import React, { useState, useEffect, forwardRef } from 'react';
import { Plus, Save, X, Camera, AlertCircle, Check } from 'lucide-react';

// Items that have size variants
const SIZED_ITEMS = ['T-Shirt', 'Long Sleeve', 'Hoodie', 'Hat', 'Tank Top', 'Crewneck'];

// Available sizes for sized items
const AVAILABLE_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];

// Currency symbols
const CURRENCY_SYMBOLS = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  CAD: 'C$',
  AUD: 'A$',
  JPY: '¥',
  CHF: 'CHF',
  SEK: 'kr',
  NOK: 'kr',
  DKK: 'kr',
  MXN: '$',
  BRL: 'R$'
};

const InventoryForm = forwardRef(({
  onSubmit,
  initialValues,
  isEditing,
  onCancel,
  itemTypes,
  sizes,
  currency = 'USD'
}, ref) => {
  const defaultValues = {
    name: '', // Custom item name (e.g., "Tour 2024 Tee")
    item: 'T-Shirt', // Category/type
    sizes: AVAILABLE_SIZES.map(size => ({
      size,
      quantity: '',
      sku: '',
      enabled: ['S', 'M', 'L', 'XL'].includes(size)
    })),
    quantity: '',
    sku: '',
    cost: '',
    sellPrice: '',
    reorderPoint: '5',
    imageUrl: '',
    notes: ''
  };

  const [form, setForm] = useState(defaultValues);
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState('');

  const currencySymbol = CURRENCY_SYMBOLS[currency] || '$';

  const isExistingSizedItem = initialValues && (
    initialValues.hasSizes || 
    (initialValues.size && typeof initialValues.size === 'string' && initialValues.size.startsWith('['))
  );
  const isSizedItem = SIZED_ITEMS.includes(form.item) || isExistingSizedItem;

  useEffect(() => {
    if (initialValues) {
      let parsedSizes = null;
      if (Array.isArray(initialValues.sizes)) {
        parsedSizes = initialValues.sizes;
      } else if (initialValues.size && typeof initialValues.size === 'string' && initialValues.size.startsWith('[')) {
        try {
          parsedSizes = JSON.parse(initialValues.size);
        } catch (e) {
          parsedSizes = null;
        }
      }
      
      if (parsedSizes && parsedSizes.length > 0) {
        setForm({
          ...defaultValues,
          ...initialValues,
          name: initialValues.name || '',
          item: initialValues.item,
          cost: initialValues.cost || initialValues.msrp || '',
          sizes: AVAILABLE_SIZES.map(size => {
            const existing = parsedSizes.find(s => s.size === size);
            return existing 
              ? { ...existing, enabled: true }
              : { size, quantity: '', sku: '', enabled: false };
          })
        });
      } else {
        const legacySize = initialValues.size || 'M';
        setForm({
          ...defaultValues,
          ...initialValues,
          name: initialValues.name || '',
          cost: initialValues.cost || initialValues.msrp || '',
          quantity: initialValues.quantity || '',
          sku: initialValues.sku || '',
          sizes: AVAILABLE_SIZES.map(size => ({
            size,
            quantity: size === legacySize ? (initialValues.quantity || '') : '',
            sku: size === legacySize ? (initialValues.sku || '') : '',
            enabled: size === legacySize
          }))
        });
      }
      setImagePreview(initialValues.imageUrl || '');
    } else {
      setForm(defaultValues);
      setImagePreview('');
    }
  }, [initialValues]);

  useEffect(() => {
    const nowSized = SIZED_ITEMS.includes(form.item);
    if (!nowSized) {
      setForm(prev => ({
        ...prev,
        quantity: prev.quantity || '',
        sku: prev.sku || ''
      }));
    }
  }, [form.item]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!form.item) {
      newErrors.item = 'Item type is required';
    }

    if (isSizedItem) {
      const enabledSizes = form.sizes.filter(s => s.enabled);
      if (enabledSizes.length === 0) {
        newErrors.sizes = 'Select at least one size';
      } else {
        const hasQuantity = enabledSizes.some(s => s.quantity && parseInt(s.quantity) >= 0);
        if (!hasQuantity) {
          newErrors.sizes = 'Enter quantity for at least one size';
        }
      }
    } else {
      if (!form.quantity || parseInt(form.quantity) < 0) {
        newErrors.quantity = 'Valid quantity required (0 or more)';
      }
    }
    
    if (!form.cost || parseFloat(form.cost) < 0) {
      newErrors.cost = 'Valid cost required';
    }
    
    if (!form.sellPrice || parseFloat(form.sellPrice) <= 0) {
      newErrors.sellPrice = 'Valid sell price required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      let submitData;
      
      if (isSizedItem) {
        const activeSizes = form.sizes
          .filter(s => s.enabled && s.quantity !== '')
          .map(s => ({
            size: s.size,
            quantity: parseInt(s.quantity) || 0,
            sku: s.sku || ''
          }));
        
        submitData = {
          name: form.name.trim() || '',
          item: form.item,
          sizes: activeSizes,
          cost: form.cost.toString(),
          msrp: form.cost.toString(),
          sellPrice: form.sellPrice.toString(),
          reorderPoint: (form.reorderPoint || '5').toString(),
          imageUrl: form.imageUrl,
          notes: form.notes,
          hasSizes: true
        };
      } else {
        submitData = {
          name: form.name.trim() || '',
          item: form.item,
          size: 'N/A',
          quantity: form.quantity.toString(),
          cost: form.cost.toString(),
          msrp: form.cost.toString(),
          sellPrice: form.sellPrice.toString(),
          sku: form.sku,
          reorderPoint: (form.reorderPoint || '5').toString(),
          imageUrl: form.imageUrl,
          notes: form.notes,
          hasSizes: false
        };
      }
      
      onSubmit(submitData);
      
      if (!isEditing) {
        setForm(defaultValues);
        setImagePreview('');
      }
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setForm({ ...form, imageUrl: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleReset = () => {
    setForm(defaultValues);
    setErrors({});
    setImagePreview('');
    onCancel?.();
  };

  const toggleSize = (sizeToToggle) => {
    setForm(prev => ({
      ...prev,
      sizes: prev.sizes.map(s => 
        s.size === sizeToToggle 
          ? { ...s, enabled: !s.enabled, quantity: !s.enabled ? s.quantity : '' }
          : s
      )
    }));
  };

  const updateSizeField = (size, field, value) => {
    setForm(prev => ({
      ...prev,
      sizes: prev.sizes.map(s => 
        s.size === size ? { ...s, [field]: value } : s
      )
    }));
  };

  const getMarginPreview = () => {
    if (form.cost && form.sellPrice) {
      const cost = parseFloat(form.cost);
      const sell = parseFloat(form.sellPrice);
      if (sell > 0) {
        const margin = ((sell - cost) / sell) * 100;
        const profit = sell - cost;
        return {
          value: margin.toFixed(1),
          isPositive: margin >= 0,
          profit: profit.toFixed(2)
        };
      }
    }
    return null;
  };

  const getTotalQuantity = () => {
    if (isSizedItem) {
      return form.sizes
        .filter(s => s.enabled)
        .reduce((sum, s) => sum + (parseInt(s.quantity) || 0), 0);
    }
    return parseInt(form.quantity) || 0;
  };

  const margin = getMarginPreview();
  const totalQty = getTotalQuantity();

  return (
    <div ref={ref} className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800 p-5 rounded-xl border border-slate-200 dark:border-gray-700 shadow-sm">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">
            {isEditing ? '✏️ Edit Item' : '📦 Add New Item'}
          </h2>
        </div>
        {margin && (
          <div className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${
            margin.isPositive && parseFloat(margin.value) >= 15
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' 
              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
          }`}>
            {margin.value}% margin ({currencySymbol}{margin.profit}/unit)
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Image Upload */}
          <div className="md:row-span-2">
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1.5 font-medium">Image</label>
            <div className="relative">
              <div className={`w-full aspect-square rounded-lg border-2 border-dashed ${
                imagePreview ? 'border-blue-300' : 'border-gray-300 dark:border-gray-600'
              } bg-white dark:bg-gray-800 flex items-center justify-center overflow-hidden cursor-pointer hover:border-blue-400`}>
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center p-4">
                    <Camera className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-xs text-gray-500">Upload</p>
                  </div>
                )}
                <input type="file" accept="image/*" onChange={handleImageChange} className="absolute inset-0 opacity-0 cursor-pointer" />
              </div>
              {imagePreview && (
                <button type="button" onClick={() => { setImagePreview(''); setForm({ ...form, imageUrl: '' }); }} className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full">
                  <X size={12} />
                </button>
              )}
            </div>
          </div>

          {/* Custom Item Name - NEW */}
          <div className="md:col-span-2">
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1.5 font-medium">
              Item Name <span className="text-gray-400">(e.g., "Tour 2024 Tee", "Band Logo Hoodie")</span>
            </label>
            <input 
              type="text" 
              value={form.name} 
              onChange={(e) => setForm({ ...form, name: e.target.value })} 
              placeholder="Give this item a unique name..."
              className="w-full px-3 py-2.5 border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1.5 font-medium">Category *</label>
            <select 
              value={form.item} 
              onChange={(e) => setForm({ ...form, item: e.target.value })} 
              className={`w-full px-3 py-2.5 border-2 ${errors.item ? 'border-red-400' : 'border-gray-200 dark:border-gray-600'} dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm`}
            >
              {itemTypes.map(type => (
                <option key={type} value={type}>{type} {SIZED_ITEMS.includes(type) ? '👕' : ''}</option>
              ))}
            </select>
          </div>

          {/* Cost */}
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1.5 font-medium">Cost *</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">{currencySymbol}</span>
              <input 
                type="number" 
                value={form.cost} 
                onChange={(e) => setForm({ ...form, cost: e.target.value })} 
                placeholder="0.00"
                step="0.01"
                className={`w-full pl-8 pr-3 py-2.5 border-2 ${errors.cost ? 'border-red-400' : 'border-gray-200 dark:border-gray-600'} dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm`}
              />
            </div>
          </div>

          {/* Sell Price */}
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1.5 font-medium">Sell Price *</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">{currencySymbol}</span>
              <input 
                type="number" 
                value={form.sellPrice} 
                onChange={(e) => setForm({ ...form, sellPrice: e.target.value })} 
                placeholder="0.00"
                step="0.01"
                className={`w-full pl-8 pr-3 py-2.5 border-2 ${errors.sellPrice ? 'border-red-400' : 'border-gray-200 dark:border-gray-600'} dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm`}
              />
            </div>
          </div>

          {/* Low Stock Alert */}
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1.5 font-medium">Low Stock Alert</label>
            <input 
              type="number" 
              value={form.reorderPoint} 
              onChange={(e) => setForm({ ...form, reorderPoint: e.target.value })} 
              placeholder="5"
              className="w-full px-3 py-2.5 border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm"
            />
          </div>

          {/* Non-sized items */}
          {!isSizedItem && (
            <>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1.5 font-medium">Quantity *</label>
                <input 
                  type="number" 
                  value={form.quantity} 
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })} 
                  placeholder="0"
                  className={`w-full px-3 py-2.5 border-2 ${errors.quantity ? 'border-red-400' : 'border-gray-200 dark:border-gray-600'} dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm`}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1.5 font-medium">SKU</label>
                <input 
                  type="text" 
                  value={form.sku} 
                  onChange={(e) => setForm({ ...form, sku: e.target.value })} 
                  placeholder="ABC-123"
                  className="w-full px-3 py-2.5 border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm font-mono"
                />
              </div>
            </>
          )}

          {/* Notes */}
          <div className={isSizedItem ? 'md:col-span-2' : ''}>
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1.5 font-medium">Notes</label>
            <input 
              type="text" 
              value={form.notes || ''} 
              onChange={(e) => setForm({ ...form, notes: e.target.value })} 
              placeholder="Optional notes..."
              className="w-full px-3 py-2.5 border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm"
            />
          </div>
        </div>

        {/* SIZE VARIANTS */}
        {isSizedItem && (
          <div className="mt-5 pt-5 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">📏 Size Variants</label>
              <span className="text-xs text-gray-500">Total: <span className="font-bold text-blue-600">{totalQty}</span></span>
            </div>
            
            {errors.sizes && <p className="text-xs text-red-500 mb-3">{errors.sizes}</p>}

            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-900 text-xs font-semibold text-gray-600">
                <div className="col-span-3">Size</div>
                <div className="col-span-4">Qty</div>
                <div className="col-span-5">SKU</div>
              </div>
              
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {form.sizes.map((sizeData) => (
                  <div key={sizeData.size} className={`grid grid-cols-12 gap-2 px-3 py-2 items-center ${sizeData.enabled ? 'bg-blue-50/50 dark:bg-blue-900/10' : 'opacity-50'}`}>
                    <div className="col-span-3 flex items-center gap-2">
                      <button type="button" onClick={() => toggleSize(sizeData.size)} className={`w-6 h-6 rounded flex items-center justify-center ${sizeData.enabled ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}>
                        {sizeData.enabled && <Check size={14} />}
                      </button>
                      <span className="font-semibold">{sizeData.size}</span>
                    </div>
                    <div className="col-span-4">
                      <input type="number" value={sizeData.quantity} onChange={(e) => updateSizeField(sizeData.size, 'quantity', e.target.value)} placeholder="0" disabled={!sizeData.enabled} className="w-full px-2 py-1.5 border rounded text-sm disabled:opacity-50 dark:bg-gray-700 dark:border-gray-600" />
                    </div>
                    <div className="col-span-5">
                      <input type="text" value={sizeData.sku} onChange={(e) => updateSizeField(sizeData.size, 'sku', e.target.value)} placeholder={`SKU-${sizeData.size}`} disabled={!sizeData.enabled} className="w-full px-2 py-1.5 border rounded text-sm font-mono disabled:opacity-50 dark:bg-gray-700 dark:border-gray-600" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 mt-5 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button type="submit" className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center gap-2">
            {isEditing ? <><Save size={18} /> Update</> : <><Plus size={18} /> Add Item</>}
          </button>
          {isEditing && (
            <button type="button" onClick={handleReset} className="px-6 py-2.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg">Cancel</button>
          )}
        </div>
      </form>
    </div>
  );
});

InventoryForm.displayName = 'InventoryForm';
export default InventoryForm;